(() => {
  class LvPlungeHeroController {
    constructor(root) {
      this.root = root;
      this.panels = [...root.querySelectorAll('[data-lv-media-panel]')];
      this.thumbs = [...root.querySelectorAll('[data-lv-media-thumb]')];
      this.addons = [...root.querySelectorAll('[data-lv-addon]')];
      this.variants = this.readJson('[data-lv-product-variants]', []);
      this.stockVariants = this.readJson('[data-lv-stock-variants]', []);
      this.config = this.readJson('[data-lv-plunge-config]', {});
      this.currentVariant = this.variants.find((variant) => String(variant.id) === String(root.querySelector('[data-lv-product-variant]')?.value)) || this.variants[0];
      this.form = root.querySelector('[data-lv-plunge-form]');
      this.submitButton = root.querySelector('.lv-plunge-hero__buy');
      this.fsaDialog = root.querySelector('[data-lv-fsa-dialog]');
      this.fsaOpenButton = root.querySelector('[data-lv-fsa-open]');
      this.fsaReturnFocus = null;

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showMedia(thumb.dataset.lvMediaThumb)));
      root.querySelector('[data-lv-gallery-prev]')?.addEventListener('click', () => this.stepGallery(-1));
      root.querySelector('[data-lv-gallery-next]')?.addEventListener('click', () => this.stepGallery(1));
      root.querySelectorAll('[data-lv-option]').forEach((select) => select.addEventListener('change', () => this.updateVariant()));
      this.addons.forEach((input) => input.addEventListener('change', () => this.updateCartTotal()));
      this.form?.addEventListener('submit', (event) => this.addSelectedItems(event));
      this.fsaOpenButton?.addEventListener('click', () => this.openFsaDialog());
      this.fsaDialog?.querySelectorAll('[data-lv-fsa-close]').forEach((button) => button.addEventListener('click', () => this.closeFsaDialog()));
      this.fsaDialog?.addEventListener('click', (event) => {
        if (event.target === this.fsaDialog) this.closeFsaDialog();
      });
      this.fsaDialog?.addEventListener('close', () => this.fsaReturnFocus?.focus());
      this.updateCartTotal();
      this.updateStockScarcity(this.currentVariant);
    }

    readJson(selector, fallback) {
      try {
        return JSON.parse(this.root.querySelector(selector)?.textContent || '');
      } catch (_error) {
        return fallback;
      }
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      this.panels.forEach((panel) => {
        const active = panel.dataset.lvMediaPanel === String(mediaId);
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (!active) panel.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.lvMediaThumb === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
        if (active) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    }

    stepGallery(direction) {
      if (!this.thumbs.length) return;
      const current = Math.max(0, this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      const next = (current + direction + this.thumbs.length) % this.thumbs.length;
      this.showMedia(this.thumbs[next].dataset.lvMediaThumb);
    }

    updateVariant() {
      const selected = [...this.root.querySelectorAll('[data-lv-option]')].map((select) => select.value);
      const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
      if (!variant) return;

      this.currentVariant = variant;
      const productInput = this.root.querySelector('[data-lv-product-variant]');
      const installmentInput = this.root.querySelector('[data-lv-installment-variant]');
      if (productInput) productInput.value = variant.id;
      if (installmentInput) {
        installmentInput.value = variant.id;
        installmentInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateStockScarcity(variant);
      this.updateCartTotal();
      if (variant.featured_media?.id) this.showMedia(variant.featured_media.id);

      if (!window.Shopify?.designMode) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    updatePrice(variant) {
      const hasSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const priceNode = this.root.querySelector('[data-lv-price]');
      const compareNode = this.root.querySelector('[data-lv-compare]');
      const savingsNode = this.root.querySelector('[data-lv-savings]');
      if (priceNode) priceNode.textContent = this.formatMoney(variant.price);
      if (compareNode) {
        compareNode.textContent = hasSale ? this.formatMoney(variant.compare_at_price) : '';
        compareNode.classList.toggle('is-hidden', !hasSale);
      }
      if (savingsNode) {
        savingsNode.textContent = hasSale ? `${this.config.savingsLabel || 'Save'} ${this.formatMoney(variant.compare_at_price - variant.price)}` : '';
        savingsNode.classList.toggle('is-hidden', !hasSale);
      }
    }

    updateAvailability(variant) {
      const available = Boolean(variant.available);
      const buyText = this.root.querySelector('[data-lv-buy-text]');
      const priceWrap = this.root.querySelector('[data-lv-buy-price-wrap]');
      const stock = this.root.querySelector('[data-lv-stock]');
      this.submitButton?.toggleAttribute('disabled', !available);
      if (buyText) buyText.textContent = available ? this.config.buyLabel || 'Add selected to cart' : this.config.soldOutLabel || 'Sold out';
      if (priceWrap) priceWrap.hidden = !available;
      if (stock) {
        stock.classList.toggle('is-sold-out', !available);
        const stockText = stock.querySelector('span');
        if (stockText) stockText.textContent = available ? this.config.inStockLabel || 'In Stock' : this.config.soldOutLabel || 'Sold out';
      }
    }

    updateStockScarcity(variant) {
      const node = this.root.querySelector('[data-lv-stock-scarcity]');
      if (!node) return;

      const settings = this.config.stockScarcity || {};
      const inventory = this.stockVariants.find((item) => String(item.id) === String(variant?.id));
      const quantity = Number(inventory?.quantity || 0);
      const threshold = Number(settings.threshold || 0);
      const tracked = Boolean(inventory?.tracked);
      const isZero = quantity <= 0;
      const hasZeroMessage = Boolean(String(settings.zeroMessage || '').trim());
      const withinThreshold = !settings.hideAboveThreshold || quantity <= threshold;
      const shouldShow = Boolean(settings.enabled && tracked && ((isZero && hasZeroMessage) || (!isZero && withinThreshold)));

      node.hidden = !shouldShow;
      node.classList.toggle('is-zero', isZero);
      if (!shouldShow) return;

      const lowMessage = node.querySelector('[data-lv-scarcity-low]');
      const zeroMessage = node.querySelector('[data-lv-scarcity-zero]');
      if (lowMessage) lowMessage.hidden = isZero;
      if (zeroMessage) {
        zeroMessage.hidden = !isZero;
        zeroMessage.textContent = settings.zeroMessage || '';
      }

      if (!isZero) {
        const prefix = node.querySelector('[data-lv-scarcity-prefix]');
        const count = node.querySelector('[data-lv-scarcity-count]');
        const suffix = node.querySelector('[data-lv-scarcity-suffix]');
        if (prefix) prefix.textContent = settings.prefix || '';
        if (count) count.textContent = String(quantity);
        if (suffix) suffix.textContent = settings.suffix || '';
      }
    }

    updateCartTotal() {
      const basePrice = Number(this.currentVariant?.price || 0);
      const addOnTotal = this.addons.reduce((total, input) => {
        const required = input.dataset.required === 'true';
        const available = input.dataset.available !== 'false';
        return total + (available && (required || (input.checked && !input.disabled)) ? Number(input.dataset.price || 0) : 0);
      }, 0);
      const totalNode = this.root.querySelector('[data-lv-cart-total]');
      if (totalNode) totalNode.textContent = this.formatMoney(basePrice + addOnTotal);
    }

    selectedItems() {
      const quantities = new Map();
      const mainId = String(this.currentVariant?.id || this.root.querySelector('[data-lv-product-variant]')?.value || '');
      if (mainId) quantities.set(mainId, 1);
      this.addons.forEach((input) => {
        const required = input.dataset.required === 'true';
        const available = input.dataset.available !== 'false';
        if (!available || !input.dataset.variantId || (!required && (!input.checked || input.disabled))) return;
        const id = String(input.dataset.variantId);
        quantities.set(id, (quantities.get(id) || 0) + 1);
      });
      return [...quantities].map(([id, quantity]) => ({ id, quantity }));
    }

    async addSelectedItems(event) {
      event.preventDefault();
      if (window.Shopify?.designMode) return;
      if (!this.currentVariant?.available || this.submitButton?.disabled) return;

      const errorNode = this.root.querySelector('[data-lv-form-error]');
      if (errorNode) {
        errorNode.hidden = true;
        errorNode.textContent = '';
      }
      this.submitButton?.setAttribute('aria-busy', 'true');
      this.submitButton?.classList.add('is-loading');
      this.submitButton?.setAttribute('disabled', '');

      try {
        const baseUrl = this.config.cartAddUrl || '/cart/add';
        const addUrl = baseUrl.endsWith('.js') ? baseUrl : `${baseUrl}.js`;
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.selectedItems() })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.config.errorMessage);
        window.location.assign(this.config.cartUrl || '/cart');
      } catch (error) {
        if (errorNode) {
          errorNode.textContent = error.message || this.config.errorMessage || "We couldn't add those items. Please try again.";
          errorNode.hidden = false;
        }
        this.submitButton?.removeAttribute('disabled');
        this.submitButton?.removeAttribute('aria-busy');
        this.submitButton?.classList.remove('is-loading');
      }
    }

    openFsaDialog() {
      if (!this.fsaDialog) return;
      this.fsaReturnFocus = document.activeElement;
      if (typeof this.fsaDialog.showModal === 'function') {
        if (!this.fsaDialog.open) this.fsaDialog.showModal();
      } else {
        this.fsaDialog.setAttribute('open', '');
      }
    }

    closeFsaDialog() {
      if (!this.fsaDialog) return;
      if (typeof this.fsaDialog.close === 'function' && this.fsaDialog.open) {
        this.fsaDialog.close();
      } else {
        this.fsaDialog.removeAttribute('open');
        this.fsaReturnFocus?.focus();
      }
    }

    formatMoney(cents) {
      const format = this.config.moneyFormat || '${{amount}}';
      const value = (Number(cents || 0) / 100).toFixed(2);
      const noDecimals = Math.round(Number(cents || 0) / 100).toString();
      return format
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{amount}}', value)
        .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals);
    }
  }

  const initLvPlungeHeroes = (scope = document) => {
    scope.querySelectorAll('[data-lv-plunge-hero]:not([data-lv-initialized])').forEach((root) => {
      root.dataset.lvInitialized = 'true';
      new LvPlungeHeroController(root);
    });
  };

  initLvPlungeHeroes();
  document.addEventListener('shopify:section:load', (event) => initLvPlungeHeroes(event.target));
})();
